import type { TopLevelNavColumnItem } from '../types';
import type { KeyboardEvent } from 'react';

import { memo } from 'react';
import isEqual from 'react-fast-compare';

import NewWindowIcon from '../../../core/icons/NewWindowIcon';
import { S } from '../TopLevelNavigation.styled';
import { getOpensInNewTabLabel, isExternalLink } from '../utils';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { getNavItemPath } from '~/utilities/helpers/getNavItemPath';
import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';

type TopLevelNavigationColumnProps = {
  column: TopLevelNavColumnItem;
  columnIndex: number;
  activeCategoryIndex: number | null;
  categoryLabel: string;
  activateLinkWithSpace: (event: KeyboardEvent<HTMLAnchorElement>) => void;
};

const TopLevelNavigationColumnComponent = ({
  column,
  columnIndex,
  activeCategoryIndex,
  categoryLabel,
  activateLinkWithSpace,
}: TopLevelNavigationColumnProps) => {
  const { topLevelNavigationContent } = useStaticContext();
  const { track } = useTopNavTracking();
  // Localized per locale in the Top Level Navigation Links content.
  const opensInNewTabLabel = getOpensInNewTabLabel(topLevelNavigationContent?.opensInNewTabLabel);
  const headingLabel = column.heading?.label ?? '';
  const headingId = `top-level-navigation-heading-${activeCategoryIndex}-${columnIndex}`;
  const headingPath = [categoryLabel, headingLabel];

  return (
    <S.Column>
      <S.Heading id={headingId} $visuallyHidden={!headingLabel}>
        {headingLabel && column.heading?.url ? (
          <S.HeadingLink
            href={column.heading.url}
            data-testid="top-level-navigation-heading-link"
            target={column.heading.target}
            onKeyDown={activateLinkWithSpace}
            onClick={() =>
              track({
                event: 'nav_interaction',
                element: 'nav_item',
                nav_item_label: headingLabel,
                nav_item_path: getNavItemPath(headingPath),
                nav_level: headingPath.length,
                interaction_type: 'click',
              })
            }
          >
            {headingLabel}
          </S.HeadingLink>
        ) : (
          <S.HeadingText>{headingLabel}</S.HeadingText>
        )}
      </S.Heading>
      <S.Groups>
        {column.groups.map(group => (
          <S.LinkList key={group.id} aria-labelledby={headingLabel ? headingId : undefined}>
            {group.links.map(link => {
              if (!link?.url) {
                return null;
              }

              const linkLabel = link.label;
              const linkPath = headingLabel
                ? [...headingPath, linkLabel]
                : [categoryLabel, linkLabel];
              const isExternal = isExternalLink(link.url, link.isExternal, link.target);

              return (
                <S.LinkItem key={link.id}>
                  <S.Link
                    href={link.url}
                    data-testid="top-level-navigation-panel-link"
                    target={isExternal ? '_blank' : link.target}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    $color={link.color}
                    $fontWeight={link.fontWeight}
                    onKeyDown={activateLinkWithSpace}
                    onClick={() =>
                      track({
                        event: 'nav_interaction',
                        element: 'nav_item',
                        nav_item_label: linkLabel,
                        nav_item_path: getNavItemPath(linkPath),
                        nav_level: linkPath.length,
                        interaction_type: 'click',
                      })
                    }
                  >
                    <span>{linkLabel}</span>
                    {isExternal && (
                      <S.ExternalIcon aria-hidden="true">
                        <NewWindowIcon />
                      </S.ExternalIcon>
                    )}
                    {isExternal && <S.VisuallyHidden>{opensInNewTabLabel}</S.VisuallyHidden>}
                  </S.Link>
                </S.LinkItem>
              );
            })}
          </S.LinkList>
        ))}
      </S.Groups>
    </S.Column>
  );
};

export const TopLevelNavigationColumn = memo(TopLevelNavigationColumnComponent, isEqual);
